import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type Theme = 'light' | 'dark' | 'system';

export type DrawingMode = 'none' | 'polygon' | 'rectangle';

export interface MapViewport {
  center: [number, number]; // [lng, lat]
  zoom: number;
  pitch?: number;
  bearing?: number;
}

export type LayerId =
  | 'satellite'
  | 'field_boundaries'
  | 'ndvi_overlay'
  | 'evi_overlay'
  | 'alerts'
  | 'sar_backscatter'
  | 'condition'
  | 'soil_moisture'
  | 'events';

interface UIState {
  // Sidebar
  sidebarOpen: boolean;
  sidebarCollapsed: boolean;

  // Selection
  selectedAssetId: string | null;
  selectedAoiId: string | null;
  selectedCountyFips: string | null;

  // Map
  mapViewport: MapViewport;
  selectedLayers: LayerId[];
  drawingMode: DrawingMode;

  // Theme
  theme: Theme;

  // Notifications panel
  notificationsPanelOpen: boolean;

  // Actions
  setSidebarOpen: (open: boolean) => void;
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  toggleSidebarCollapsed: () => void;
  setSelectedAssetId: (id: string | null) => void;
  setSelectedAoiId: (id: string | null) => void;
  setSelectedCountyFips: (fips: string | null) => void;
  setMapViewport: (viewport: Partial<MapViewport>) => void;
  setSelectedLayers: (layers: LayerId[]) => void;
  toggleLayer: (layerId: LayerId) => void;
  setDrawingMode: (mode: DrawingMode) => void;
  setTheme: (theme: Theme) => void;
  toggleNotificationsPanel: () => void;
  setNotificationsPanelOpen: (open: boolean) => void;
}

const DEFAULT_VIEWPORT: MapViewport = {
  center: [-93.5, 41.5], // US Midwest
  zoom: 6,
  pitch: 0,
  bearing: 0,
};

export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      // Initial state
      sidebarOpen: true,
      sidebarCollapsed: false,
      selectedAssetId: null,
      selectedAoiId: null,
      selectedCountyFips: null,
      mapViewport: DEFAULT_VIEWPORT,
      selectedLayers: ['satellite', 'field_boundaries'] as LayerId[],
      drawingMode: 'none' as DrawingMode,
      theme: 'light' as Theme,
      notificationsPanelOpen: false,

      // Actions
      setSidebarOpen: (open) => set({ sidebarOpen: open }),
      toggleSidebar: () => set((s) => ({ sidebarOpen: !s.sidebarOpen })),
      setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),
      toggleSidebarCollapsed: () =>
        set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
      setSelectedAssetId: (id) => set({ selectedAssetId: id }),
      setSelectedAoiId: (id) => set({ selectedAoiId: id }),
      setSelectedCountyFips: (fips) => set({ selectedCountyFips: fips }),
      setMapViewport: (viewport) =>
        set((s) => ({ mapViewport: { ...s.mapViewport, ...viewport } })),
      setSelectedLayers: (layers) => set({ selectedLayers: layers }),
      toggleLayer: (layerId) =>
        set((s) => ({
          selectedLayers: s.selectedLayers.includes(layerId)
            ? s.selectedLayers.filter((l) => l !== layerId)
            : [...s.selectedLayers, layerId],
        })),
      setDrawingMode: (mode) => set({ drawingMode: mode }),
      setTheme: (theme) => set({ theme }),
      toggleNotificationsPanel: () =>
        set((s) => ({ notificationsPanelOpen: !s.notificationsPanelOpen })),
      setNotificationsPanelOpen: (open) =>
        set({ notificationsPanelOpen: open }),
    }),
    {
      name: 'terrayield-ui',
      partialize: (s) => ({
        sidebarCollapsed: s.sidebarCollapsed,
        selectedLayers: s.selectedLayers,
        mapViewport: s.mapViewport,
        theme: s.theme,
      }),
    },
  ),
);
